import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks';
import { getEditRoleStaff, getRoleByIdAsync, getRolePermissionListStaff, updateRoleAsync, updateRoleStaff } from 'src/redux/actions/manageUserActions';
import useAppToast from 'src/hooks/useAppToast'
import Loader from 'src/utils/Loader';
import {
  DEFAULT_PERMISSIONS,
  PermissionKey,
  PermissionValue,
  UserPermissionItem,
  mapPermissionsToApiPayload
} from './rolePermissionConfig';
import PermissionCards from './PermissionCards';
import './RoleModal.scss';
import { toast } from 'react-toastify';
import { usePermission } from 'src/hooks/usePermission';

const EditRoleModal = ({ show, handleClose, role, fetchData, isLoginAdminAcess }) => {
  const dispatch = useAppDispatch();
  const {isRead, isWrite, isDelete} = usePermission();
  const { errorToast, successToast } = useAppToast();

  const [roleName, setRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<Record<PermissionKey, PermissionValue>>({
    ...DEFAULT_PERMISSIONS,
  });
  const [userPermissionList, setUserPermissionList] = useState<UserPermissionItem[]>([]);
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData);
  const isSuperuser = fetchedUserAccountData?.is_superuser || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoading(true);
        const response = await dispatch(getRolePermissionListStaff()).unwrap();
        setUserPermissionList(response.data);
      } catch (error) {
        errorToast('Failed to load roles.');
      } finally {
        setLoading(false);
      }
    };
    if (!isSuperuser) {
      fetchRoles();
    }
  }, []);

  useEffect(() => {
    if (!show) {
      setRoleName('');
      setPermissions({ ...DEFAULT_PERMISSIONS });
      return;
    }
    if (!role?.id) return;
    const fetchRoleDetails = async () => {
      if (isSuperuser) {
        try {
          setLoading(true);
          const response = await dispatch(getRoleByIdAsync({ id: role.id }));
          const data = response.payload?.data;

          if (data) {
            setRoleName(data.name || '');

            const normalizeAccess = (access: string | undefined, section: PermissionKey): PermissionValue => {
              const formatted = access?.toUpperCase();
              // if (formatted === 'SUPERADMIN' && section === "report") {
              if (formatted === 'SUPERADMIN' && section !== 'customer') {
                return 'WRITE';
              }
              if (formatted === 'READ' || formatted === 'WRITE' || formatted === 'SUPERADMIN') {
                return formatted as PermissionValue;
              }
              return 'RESTRICT';
            };

            setPermissions({
              customer: normalizeAccess(data?.customer_access, 'customer'),
              managePlan: normalizeAccess(data?.manage_plan_access, 'managePlan'),
              report: normalizeAccess(data?.report_access, 'report'),
              userMgmt: normalizeAccess(data?.user_access, 'userMgmt'),
            });
          }
        } catch (err) {
          errorToast('Failed to fetch role details.');
        } finally {
          setLoading(false);
        }
      } else {
        try {
          setLoading(true);
          const response = await dispatch(getEditRoleStaff({ id: role.id }));
          const data = response.payload?.data;
          setRoleName(data.name || '');
          const fetchedFlowPermissions = data?.flow_and_permissions || {};
          const updatedUserPermissionList = (userPermissionList || []).map((item) => {
            const key = item.key;
            const selectedValue = fetchedFlowPermissions[key]?.toUpperCase() || 'RESTRICT';
            return {
              ...item,
              permissions: item.permissions.map((perm: any) => ({
                ...perm,
                isActive: perm.value === selectedValue,
              })),
              defaultValue: selectedValue,
            };
          });
          setUserPermissionList(updatedUserPermissionList);
          // Set permissions state for pre-selection in PermissionCards
          const defaultPermissionsForStaff: Record<PermissionKey, PermissionValue> = {
            customer: 'RESTRICT', managePlan: 'RESTRICT', report: 'RESTRICT', userMgmt: 'RESTRICT',
          };
          updatedUserPermissionList.forEach((item) => {
            const key = item.key as PermissionKey;
            const selectedValue = fetchedFlowPermissions[key]?.toUpperCase() as PermissionValue;
            if (selectedValue === 'READ' || selectedValue === 'WRITE' || selectedValue === 'SUPERADMIN' || selectedValue === 'FULL') {
              defaultPermissionsForStaff[key] = selectedValue;
            }
          });
          setPermissions(defaultPermissionsForStaff);
        } catch (err) {
          errorToast('Failed to fetch role details.');
        } finally {
          setLoading(false);
        }
      }

    };

    fetchRoleDetails();
  }, [show, role?.id]);

  const handlePermissionChange = (section: PermissionKey, value: PermissionValue) => {
    setPermissions((prev) => ({
      ...prev,
      [section]: value,
    }));
  };

  const handleRoleSave = async () => {
    if (!role?.id) {
      errorToast('Role details are missing. Please reopen the modal and try again.');
      return;
    }
    if (isSuperuser) {
      const permissionPayload = mapPermissionsToApiPayload(permissions);
      const payload = {
        id: role.id,
        name: roleName,
        ...permissionPayload,
      };

      if (!roleName.trim()) {
        errorToast('Role Name is required.');
        return;
      }
      setLoading(true);
      await dispatch(updateRoleAsync(payload))
        .then((response) => {
          if (response.payload.status_code == 200) {
            successToast(response.payload.massage);
          }
          if (response?.payload?.data?.status_code === 400) {
            errorToast(response?.payload?.data?.massage);
          }
          fetchData();
          handleClose();
        })
        .catch(() => {
          errorToast('Failed to update role.');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      try {
         const filteredPermissions = Object.fromEntries(
        userPermissionList.map(({ key }) => [key, permissions[key] ?? "RESTRICT"]));

        const payload = {
          ...filteredPermissions,
          name: roleName,
          is_active: true,
        };

        const response = await dispatch(updateRoleStaff({ id: role.id, body: payload })).unwrap();
        if (response.status_code === 200) {
          toast.success(response.massage || 'Role updated successfully');
          fetchData();
          handleClose(); // close only on success
        } else {
          toast.error(response.massage || 'Failed to update role');
        }
      } catch (err: any) {
        console.error(err);
        toast.error(err?.data?.massage || err?.message || 'Failed to update role');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      dialogClassName="role-modal__dialog role-modal"
      contentClassName="role-modal__content"
    >
      <Modal.Header closeButton className="role-modal__header">
        <div>
          <h2 className="role-modal__title">Edit Role</h2>
          <p className="role-modal__subtitle">Define permissions and access levels for this role.</p>
        </div>
      </Modal.Header>
      <Modal.Body className="role-modal__body">
        {loading ? (
          <div className="text-center py-4">
            <Loader variant="section" />
          </div>
        ) : (
          <Form>
            <Form.Group controlId="formRoleName" className="role-modal__form-group">
              <Form.Label className="role-modal__label required">Role Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Customer Support, Senior Admin"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                required
                className="role-modal__input"
              />
            </Form.Group>

            <h6 className="role-modal__permissions-title">Permissions</h6>
            <PermissionCards permissions={permissions} onChange={handlePermissionChange} isLoginAdminAcess={isLoginAdminAcess} userPermissionList={userPermissionList} />
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="role-modal__footer">
        <Button className="role-modal__cancel" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {isWrite && <Button className="role-modal__primary" onClick={handleRoleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Update Role'}
        </Button>}
      </Modal.Footer>
    </Modal>
  );
};

export default EditRoleModal;
